import { translator } from "../../../../translation/translator";
import { notifier } from "../../../../core/notifier/notifier";
import commonUtil from "../../../../util/common-utils";
import { spinner } from "../../../../util/spinner/spinner";
import { CategoryEditorHandler as CategoryEditorCore } from "./category-editor/category-editor-handler";
import { CategoryReviewEditorForm } from "./category-review-editor-form";
import { CategoryReviewEditorRender } from "./category-review-editor-render";
import { ReviewConfigEditorHandler as ReviewConfigEditorCore } from "./review-config-editor/review-config-editor-handler";
type UnknownRecord = Record<string, unknown>;
type CategoryReviewEditorParams = Record<string, unknown>;
type TranslationParams = Record<string, string | number>;

const toInt = (value: unknown, fallback = 0): number => commonUtil.toInt(value, fallback);
const toNumberOrNull = (value: unknown): number | null => commonUtil.toNumberOrNull(value);
const isRecord = (value: unknown): value is UnknownRecord => commonUtil.isRecord(value);
const extractItems = (payload: unknown): UnknownRecord[] => commonUtil.extractItems(payload) as UnknownRecord[];
const toErrorMessage = (error: unknown, fallback: string): string =>
    error instanceof Error ? error.message : fallback;

function flattenCategories(menuData: unknown): UnknownRecord[] {
    if (!isRecord(menuData)) return [];

    if (Array.isArray(menuData.categories)) {
        return menuData.categories.filter((c) => isRecord(c));
    }

    const parentCategories = Array.isArray(menuData.parent_categories) ? menuData.parent_categories : [];
    const result = [];

    parentCategories.forEach((p) => {
        if (!isRecord(p)) return;
        result.push(p);

        const children = Array.isArray(p.categories) ? p.categories : [];
        children.forEach((c) => {
            if (isRecord(c)) result.push(c);
        });
    });

    return result;
}

const OTHERS_PARENT_CATEGORY_ID = 9900;
const OTHERS_CHILD_CATEGORY_ID = 9999;
const OTHERS_SUFFIX = 99;
const OTHERS_NAME = "Others";
const SEARCH_CATEGORY_MODAL_DATA_UPDATED_EVENT = "search-category-modal:data-updated";
const CATEGORY_REVIEW_EDITOR_INLINE_SPINNER_ID = "category-review-editor-inline";

export class CategoryReviewEditorHandler {
    // This handler intentionally stores many heterogeneous fields during modal lifecycle.
    // A full structural type exists only after the editor modules are split further.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;

    public constructor(params: CategoryReviewEditorParams) {
        const modalEl = params.modalEl instanceof HTMLElement ? params.modalEl : document.body;
        this.form = new CategoryReviewEditorForm(document, modalEl);
        this.api = params.api;
        this.overlayEl = params.overlayEl;
        this.modalEl = params.modalEl;
        this.tabParentEl = params.tabParentEl;
        this.tabChildEl = params.tabChildEl;
        this.tabReviewEl = params.tabReviewEl;
        this.panelParentEl = params.panelParentEl;
        this.panelChildEl = params.panelChildEl;
        this.panelReviewEl = params.panelReviewEl;
        this.saveBtnEl = params.saveBtnEl;
        this.addBtnEl = params.addBtnEl;
        this.statusEl = params.statusEl;
        this.addEntryModalEl = params.addEntryModalEl;
        this.addEntryCodeEl = params.addEntryCodeEl;
        this.addEntryNameEl = params.addEntryNameEl;
        this.addEntrySubmitBtnEl = params.addEntrySubmitBtnEl;
        this.addEntryCloseBtnEls = Array.isArray(params.addEntryCloseBtnEls) ? params.addEntryCloseBtnEls : [];
        this.questionOptionModalEl = params.questionOptionModalEl;
        this.questionOptionRowsEl = params.questionOptionRowsEl;
        this.questionOptionAddRowBtnEl = params.questionOptionAddRowBtnEl;
        this.questionOptionSubmitBtnEl = params.questionOptionSubmitBtnEl;
        this.questionOptionCloseBtnEls = Array.isArray(params.questionOptionCloseBtnEls)
            ? params.questionOptionCloseBtnEls
            : [];
        this.reviewSettingsModalEl = params.reviewSettingsModalEl;
        this.reviewSettingsTitleEl = params.reviewSettingsTitleEl;
        this.reviewSettingsPanelEl = params.reviewSettingsPanelEl;
        this.reviewSettingsSaveBtnEl = params.reviewSettingsSaveBtnEl;
        this.reviewSettingsAddBtnEl = params.reviewSettingsAddBtnEl;
        this.reviewSettingsAddQuestionBtnEl = params.reviewSettingsAddQuestionBtnEl;
        this.reviewSettingsCloseBtnEls = Array.isArray(params.reviewSettingsCloseBtnEls)
            ? params.reviewSettingsCloseBtnEls
            : [];
        this.renderer = new CategoryReviewEditorRender({
            getQuestionLabel: (question) => this.getQuestionLabel(question),
        });
        this.categoryEditorCore = new CategoryEditorCore(this);
        this.reviewConfigCore = new ReviewConfigEditorCore(this);

        this.activeTab = "parent";

        this.categories = [];
        this.parents = [];
        this.children = [];
        this.configs = [];

        this.questionsByConfig = new Map();
        this.parentById = new Map();
        this.childById = new Map();
        this.questionById = new Map();

        this.maxQuestionCount = 1;
        this.isLoading = false;
        this.isSubmitting = false;
        this.selectedChildParentId = 0;

        this.nextTempCategoryId = -1;
        this.nextTempQuestionId = -1;
        this.latestSearchMenuData = null;
        this.activeQuestionOptionTarget = null;
        this.reviewSettingsConfigId = 0;

        this.reviewSettingsTargetChildId = 0;
    }

    public handleOpenClick(event) {
        event.preventDefault();
        event.stopPropagation();
        void this.open();
    }

    public handleCloseClick(event) {
        event.preventDefault();
        this.close();
    }

    public handleOverlayClick(event) {
        if (event.target !== this.overlayEl) return;
        this.close();
    }

    public handleTabParentClick(event) {
        event.preventDefault();
        this.switchTab("parent");
    }

    public handleTabChildClick(event) {
        event.preventDefault();
        this.switchTab("child");
    }

    public handleTabReviewClick(event) {
        event.preventDefault();
        this.switchTab("review");
    }

    public async handleSaveClick(event) {
        event.preventDefault();
        await this.saveAllChanges();
    }

    public async saveAllChanges() {
        const saveOptions = {
            reloadAfterSave: false,
            suppressNoChangeStatus: true,
            suppressSuccessStatus: true,
        };

        const parentChanged = await this.saveParentCategories(saveOptions);
        const configChanged = await this.saveReviewConfigs(saveOptions);

        const hasUnsavedNewConfigs = this.configs.some(
            (config) => config?._isNew === true && config?._isDeleted !== true,
        );
        if (hasUnsavedNewConfigs) {
            this.setStatusTranslated("category_review_editor_status_review_config_save_failed", "error");
            notifier.error("category_review_editor_status_review_config_save_failed");
            return;
        }

        const childChanged = await this.saveChildCategories(saveOptions);
        const questionChanged = await this.saveReviewQuestions(saveOptions);
        const totalChanged = parentChanged + configChanged + childChanged + questionChanged;

        if (totalChanged <= 0) {
            this.setStatusTranslated("category_review_editor_status_no_changes", "ok");
            return;
        }

        await this.reloadData();
        const successMessage = translator.getTranslation("category_review_editor_status_saved_changes", {
            count: totalChanged,
        });
        this.setStatus(successMessage, "ok");
        notifier.success(successMessage);
        if (totalChanged > 0) {
            this.notifySearchCategoryModalDataUpdated();
        }
    }

    public isReviewSettingsOpen() {
        return this.reviewConfigCore.isReviewSettingsOpen();
    }

    public openReviewSettingsModal(configId: number) {
        this.reviewConfigCore.openReviewSettingsModal(configId);
    }

    public closeReviewSettingsModal() {
        this.reviewConfigCore.closeReviewSettingsModal();
    }

    public handleReviewSettingsModalClick(event) {
        this.reviewConfigCore.handleReviewSettingsModalClick(event);
    }

    public handleReviewSettingsClose(event) {
        this.reviewConfigCore.handleReviewSettingsClose(event);
    }

    public async handleReviewSettingsSave(event) {
        await this.reviewConfigCore.handleReviewSettingsSave(event);
    }

    public async handleReviewSettingsAdd(event) {
        await this.reviewConfigCore.handleReviewSettingsAdd(event);
    }

    public async handleReviewSettingsAddQuestion(event) {
        await this.reviewConfigCore.handleReviewSettingsAddQuestion(event);
    }

    public async handleReviewSettingsAddConfig(event) {
        await this.reviewConfigCore.handleReviewSettingsAddConfig(event);
    }

    public ensureReviewConfigForChild(childId: number): number {
        return this.reviewConfigCore.ensureReviewConfigForChild(childId);
    }

    public getReviewConfigRow(configId: number) {
        return this.reviewConfigCore.getReviewConfigRow(configId);
    }

    public canSelectReviewConfig(configId: number) {
        return this.reviewConfigCore.canSelectReviewConfig(configId);
    }

    public handleReviewSettingsSelectConfig(configId: number) {
        this.reviewConfigCore.handleReviewSettingsSelectConfig(configId);
    }

    public getNextAvailableReviewConfigId() {
        return this.reviewConfigCore.getNextAvailableReviewConfigId();
    }

    public extractReviewConfigIdFromCreateResponse(payload) {
        return this.reviewConfigCore.extractReviewConfigIdFromCreateResponse(payload);
    }

    public remapReviewConfigIdInLocalState(oldId, newId) {
        this.reviewConfigCore.remapReviewConfigIdInLocalState(oldId, newId);
    }

    public scrollReviewSettingsTableToLeft() {
        this.reviewConfigCore.scrollReviewSettingsTableToLeft();
    }

    public animateHorizontalScroll(wrapEl, toLeft, durationMs = 260) {
        this.reviewConfigCore.animateHorizontalScroll(wrapEl, toLeft, durationMs);
    }

    public scrollReviewSettingsTableToRight() {
        this.reviewConfigCore.scrollReviewSettingsTableToRight();
    }

    public scrollWrapToElementCenter(wrapEl, targetEl, behavior = "smooth") {
        this.reviewConfigCore.scrollWrapToElementCenter(wrapEl, targetEl, behavior);
    }

    public focusNewReviewQuestionEditor(questionId) {
        this.reviewConfigCore.focusNewReviewQuestionEditor(questionId);
    }

    public notifySearchCategoryModalDataUpdated() {
        if (!isRecord(this.latestSearchMenuData)) return;
        document.dispatchEvent(
            new CustomEvent(SEARCH_CATEGORY_MODAL_DATA_UPDATED_EVENT, {
                detail: {
                    apiData: this.latestSearchMenuData,
                },
            }),
        );
    }

    public switchTab(tabKey) {
        this.categoryEditorCore.switchTab(tabKey);
    }

    public async open() {
        await this.categoryEditorCore.open();
    }

    public close() {
        this.categoryEditorCore.close();
    }

    public syncFooterAddButton() {
        this.categoryEditorCore.syncFooterAddButton();
    }

    public isLockedOthersChild(item) {
        const id = toInt(item?.category_id, 0);
        const normalizedName = String(item?._draftName ?? item?.category_name ?? "")
            .trim()
            .toLowerCase();
        if (id <= 0) return false;
        return id % 100 === OTHERS_SUFFIX && normalizedName === OTHERS_NAME.toLowerCase();
    }

    public getOthersChildIdFromParentId(parentId) {
        return this.categoryEditorCore.getOthersChildIdFromParentId(parentId);
    }

    public collectActiveCategoryIds() {
        return this.categoryEditorCore.collectActiveCategoryIds();
    }

    public ensureChildParentSelection() {
        const activeParentIds = this.parents
            .filter((parent) => !parent._isDeleted)
            .map((parent) => toInt(parent.category_id, 0))
            .filter((id) => id > 0);

        if (activeParentIds.length === 0) {
            this.selectedChildParentId = 0;
            return;
        }

        if (!activeParentIds.includes(toInt(this.selectedChildParentId, 0))) {
            this.selectedChildParentId = activeParentIds[0];
        }
    }

    public getNextParentRawCode() {
        return this.categoryEditorCore.getNextParentRawCode();
    }

    public getNextChildRawCode(parentId) {
        return this.categoryEditorCore.getNextChildRawCode(parentId);
    }

    public updateAddEntryModalContext() {
        this.categoryEditorCore.updateAddEntryModalContext();
    }

    public handleAddEntryModalInput(event) {
        this.categoryEditorCore.handleAddEntryModalInput(event);
    }

    public openAddEntryModal() {
        this.categoryEditorCore.openAddEntryModal();
    }

    public closeAddEntryModal() {
        this.categoryEditorCore.closeAddEntryModal();
    }

    public formatCommaSeparatedOptions(list) {
        return this.categoryEditorCore.formatCommaSeparatedOptions(list);
    }

    public buildQuestionOptionRows(question) {
        return this.categoryEditorCore.buildQuestionOptionRows(question);
    }

    public renderQuestionOptionRows(rows) {
        this.categoryEditorCore.renderQuestionOptionRows(rows);
    }

    public collectQuestionOptionRowsFromModal() {
        return this.categoryEditorCore.collectQuestionOptionRowsFromModal();
    }

    public getQuestionOptionsByLang(question, lang) {
        return this.categoryEditorCore.getQuestionOptionsByLang(question, lang);
    }

    public openQuestionOptionModal(configId: number, questionId: number) {
        this.categoryEditorCore.openQuestionOptionModal(configId, questionId);
    }

    public closeQuestionOptionModal() {
        this.categoryEditorCore.closeQuestionOptionModal();
    }

    public handleQuestionOptionModalClick(event) {
        this.categoryEditorCore.handleQuestionOptionModalClick(event);
    }

    public handleQuestionOptionClose(event) {
        this.categoryEditorCore.handleQuestionOptionClose(event);
    }

    public handleQuestionOptionSubmit(event) {
        this.categoryEditorCore.handleQuestionOptionSubmit(event);
    }

    public handleFooterAddClick(event) {
        this.categoryEditorCore.handleFooterAddClick(event);
    }

    public handleAddEntryModalClick(event) {
        this.categoryEditorCore.handleAddEntryModalClick(event);
    }

    public handleAddEntryClose(event) {
        this.categoryEditorCore.handleAddEntryClose(event);
    }

    public handleAddEntrySubmit(event) {
        this.categoryEditorCore.handleAddEntrySubmit(event);
    }

    public getNextTempCategoryId() {
        const id = this.nextTempCategoryId;
        this.nextTempCategoryId -= 1;
        return id;
    }

    public getNextTempQuestionId() {
        const id = this.nextTempQuestionId;
        this.nextTempQuestionId -= 1;
        return id;
    }

    public normalizeParentRow(row) {
        const categoryId = toInt(row.category_id, 0);
        const productsCount = Math.max(0, toInt(row.products_count, 0));
        return {
            ...row,
            category_id: categoryId,
            parent_category_id: null,
            category_name: String(row.category_name || ""),
            products_count: productsCount,
            _isNew: row._isNew === true,
            _isDeleted: row._isDeleted === true,
            _isEditing: row._isEditing === true,
            _draftName: row._draftName !== undefined ? String(row._draftName || "") : undefined,
            _isLocked: row._isLocked === true,
        };
    }

    public normalizeChildRow(row) {
        const categoryId = toInt(row.category_id, 0);
        const parentId = toInt(row.parent_category_id, 0);
        const configId = toInt(row.review_config_id, 0);
        const productsCount = Math.max(0, toInt(row.products_count, 0));
        return {
            ...row,
            category_id: categoryId,
            parent_category_id: parentId,
            review_config_id: configId,
            category_name: String(row.category_name || ""),
            products_count: productsCount,
            _isNew: row._isNew === true,
            _isDeleted: row._isDeleted === true,
            _isEditing: row._isEditing === true,
            _draftName: row._draftName !== undefined ? String(row._draftName || "") : undefined,
            _draftReviewConfigId:
                row._draftReviewConfigId !== undefined ? toInt(row._draftReviewConfigId, 0) : undefined,
            _isLocked: row._isLocked === true,
            _isSystemOthers: row._isSystemOthers === true,
        };
    }

    public normalizeQuestionRow(row) {
        const questionId = toInt(row.review_question_id, 0);
        const configId = toInt(row.review_config_id, 0);
        const questionTextTh = String(row.question_text_th || row.question_text || "");
        const questionTextEn = String(row.question_text_en || row.question_text || "");
        const questionTextJa = String(row.question_text_ja || row.question_text || "");
        return {
            ...row,
            review_question_id: questionId,
            review_config_id: configId,
            question_text: String(row.question_text || ""),
            data_type: String(row.data_type || "text"),
            question_text_th: questionTextTh,
            question_text_en: questionTextEn,
            question_text_ja: questionTextJa,
            option_th: Array.isArray(row.option_th) ? row.option_th : [],
            option_en: Array.isArray(row.option_en) ? row.option_en : [],
            option_ja: Array.isArray(row.option_ja) ? row.option_ja : [],
            _isNew: row._isNew === true,
            _isDeleted: row._isDeleted === true,
            _isEditing: row._isEditing === true,
            _draftDataType: row._draftDataType !== undefined ? String(row._draftDataType || "text") : undefined,
            _draftOptionTh: Array.isArray(row._draftOptionTh) ? row._draftOptionTh : undefined,
            _draftOptionEn: Array.isArray(row._draftOptionEn) ? row._draftOptionEn : undefined,
            _draftOptionJa: Array.isArray(row._draftOptionJa) ? row._draftOptionJa : undefined,
            _draftTextTh: row._draftTextTh !== undefined ? String(row._draftTextTh || "") : undefined,
            _draftTextEn: row._draftTextEn !== undefined ? String(row._draftTextEn || "") : undefined,
            _draftTextJa: row._draftTextJa !== undefined ? String(row._draftTextJa || "") : undefined,
            _isLocked: row._isLocked === true,
        };
    }

    public getCategoryProductsCount(row) {
        return Math.max(0, toInt(row?.products_count, 0));
    }

    public canDeleteParentCategory(parent) {
        if (!isRecord(parent)) return false;
        if (parent._isLocked === true) return false;
        if (parent._isNew === true) return true;

        const parentId = toInt(parent.category_id, 0);
        if (parentId <= 0) return false;

        const hasActiveChildren = this.children.some(
            (child) => !child._isDeleted && toInt(child.parent_category_id, 0) === parentId,
        );
        return !hasActiveChildren;
    }

    public canDeleteChildCategory(child) {
        if (!isRecord(child)) return false;
        if (child._isLocked === true) return false;
        return this.getCategoryProductsCount(child) === 0;
    }

    public isParentDirty(parent) {
        if (!isRecord(parent)) return false;
        const originalName = String(parent.category_name || "").trim();
        const draftName = String(parent._draftName ?? parent.category_name ?? "").trim();
        return draftName !== originalName;
    }

    public isChildDirty(child) {
        if (!isRecord(child)) return false;
        const originalName = String(child.category_name || "").trim();
        const draftName = String(child._draftName ?? child.category_name ?? "").trim();
        const originalConfigId = toInt(child.review_config_id, 0);
        const draftConfigId = toInt(child._draftReviewConfigId ?? child.review_config_id ?? 0, 0);
        return draftName !== originalName || draftConfigId !== originalConfigId;
    }

    public isQuestionDirty(question) {
        if (!isRecord(question)) return false;
        const originalTh = String(question.question_text_th ?? question.question_text ?? "").trim();
        const originalEn = String(question.question_text_en ?? question.question_text ?? "").trim();
        const originalJa = String(question.question_text_ja ?? question.question_text ?? "").trim();
        const draftTh = String(
            question._draftTextTh ?? question.question_text_th ?? question.question_text ?? "",
        ).trim();
        const draftEn = String(
            question._draftTextEn ?? question.question_text_en ?? question.question_text ?? "",
        ).trim();
        const draftJa = String(
            question._draftTextJa ?? question.question_text_ja ?? question.question_text ?? "",
        ).trim();
        const originalOptionTh = this.formatCommaSeparatedOptions(question.option_th || []);
        const originalOptionEn = this.formatCommaSeparatedOptions(question.option_en || []);
        const originalOptionJa = this.formatCommaSeparatedOptions(question.option_ja || []);
        const draftOptionTh = this.formatCommaSeparatedOptions(this.getQuestionOptionsByLang(question, "th"));
        const draftOptionEn = this.formatCommaSeparatedOptions(this.getQuestionOptionsByLang(question, "en"));
        const draftOptionJa = this.formatCommaSeparatedOptions(this.getQuestionOptionsByLang(question, "ja"));
        const originalDataType = String(question.data_type || "text").trim();
        const draftDataType = String(question._draftDataType ?? question.data_type ?? "text").trim();
        return (
            draftTh !== originalTh ||
            draftEn !== originalEn ||
            draftJa !== originalJa ||
            draftOptionTh !== originalOptionTh ||
            draftOptionEn !== originalOptionEn ||
            draftOptionJa !== originalOptionJa ||
            draftDataType !== originalDataType
        );
    }

    public cancelParentEdit(parent) {
        if (!isRecord(parent)) return;
        parent._draftName = undefined;
        parent._isEditing = false;
    }

    public cancelChildEdit(child) {
        if (!isRecord(child)) return;
        child._draftName = undefined;
        child._draftReviewConfigId = undefined;
        child._isEditing = false;
    }

    public cancelQuestionEdit(question) {
        if (!isRecord(question)) return;
        question._draftDataType = undefined;
        question._draftOptionTh = undefined;
        question._draftOptionEn = undefined;
        question._draftOptionJa = undefined;
        question._draftTextTh = undefined;
        question._draftTextEn = undefined;
        question._draftTextJa = undefined;
        question._isEditing = false;
    }

    public restoreInputFocus(selector, valueLength, selectionStart, selectionEnd) {
        const nextInput = this.form.getInputBySelector(document, selector);
        if (!(nextInput instanceof HTMLInputElement)) return;
        nextInput.focus();
        const maxPos = Math.max(0, Math.min(valueLength, nextInput.value.length));
        const start = Number.isFinite(selectionStart) ? Math.min(selectionStart, maxPos) : maxPos;
        const end = Number.isFinite(selectionEnd) ? Math.min(selectionEnd, maxPos) : start;
        try {
            nextInput.setSelectionRange(start, end);
        } catch (_error) {
            // ignore selection failures on unsupported input types
        }
    }

    public reindexLocalState() {
        this.parentById = new Map(this.parents.filter((p) => !p._isDeleted).map((p) => [toInt(p.category_id, 0), p]));

        this.childById = new Map(this.children.filter((c) => !c._isDeleted).map((c) => [toInt(c.category_id, 0), c]));

        this.questionById.clear();
        this.questionsByConfig.forEach((list) => {
            list.forEach((q) => {
                if (q._isDeleted) return;
                this.questionById.set(toInt(q.review_question_id, 0), q);
            });
        });

        this.maxQuestionCount = 1;
        this.questionsByConfig.forEach((list) => {
            const size = list.filter((q) => !q._isDeleted).length;
            if (size > this.maxQuestionCount) this.maxQuestionCount = size;
        });
    }

    public getQuestionRowById(questionId: number) {
        let found = null;
        this.questionsByConfig.forEach((list) => {
            if (found) return;
            const hit = list.find((q) => toInt(q.review_question_id, 0) === questionId);
            if (hit) found = hit;
        });
        return found;
    }

    public getConfigIdByQuestionId(questionId: number): number {
        let foundConfigId = 0;
        this.questionsByConfig.forEach((list, configId) => {
            if (foundConfigId > 0) return;
            const hit = list.find((q) => toInt(q.review_question_id, 0) === questionId);
            if (!hit) return;
            foundConfigId = toInt(configId, 0);
        });
        return foundConfigId;
    }

    public setBusy(isBusy) {
        if (this.saveBtnEl instanceof HTMLButtonElement) {
            this.saveBtnEl.disabled = isBusy;
        }
        this.syncFooterAddButton();
    }

    public setModalLoading(isLoading) {
        if (!(this.modalEl instanceof HTMLElement)) return;
        if (isLoading) {
            if (!spinner.isShowing(CATEGORY_REVIEW_EDITOR_INLINE_SPINNER_ID)) {
                spinner.show({
                    id: CATEGORY_REVIEW_EDITOR_INLINE_SPINNER_ID,
                    container: this.modalEl,
                    backdrop: false,
                    blur: false,
                    message: null,
                });
            }
            return;
        }
        spinner.hide(CATEGORY_REVIEW_EDITOR_INLINE_SPINNER_ID);
    }

    public setStatus(text, tone = "") {
        if (!(this.statusEl instanceof HTMLElement)) return;
        this.statusEl.removeAttribute("data-translate");
        this.statusEl.removeAttribute("data-parent-id");
        this.statusEl.removeAttribute("data-count");
        this.statusEl.removeAttribute("aria-label");
        this.statusEl.textContent = String(text || "");
        this.statusEl.dataset.tone = tone;
        this.setModalLoading(tone === "loading");
    }

    public setLoadingStatus(translateKey = "category_review_editor_status_loading") {
        if (!(this.statusEl instanceof HTMLElement)) return;
        this.statusEl.removeAttribute("data-translate");
        this.statusEl.removeAttribute("data-parent-id");
        this.statusEl.removeAttribute("data-count");
        this.statusEl.dataset.tone = "loading";
        this.statusEl.setAttribute("aria-label", translator.translate(translateKey));
        this.statusEl.textContent = "";
        this.setModalLoading(true);
    }

    public setStatusTranslated(key, tone = "", params: TranslationParams = {}) {
        if (!(this.statusEl instanceof HTMLElement)) return;

        if (key === "category_review_editor_status_loading" || key === "processing") {
            this.setLoadingStatus(key);
            return;
        }

        this.statusEl.setAttribute("data-translate", key);
        this.statusEl.removeAttribute("data-parent-id");
        this.statusEl.removeAttribute("data-count");

        if (params.parentId !== undefined) {
            this.statusEl.setAttribute("data-parent-id", String(params.parentId));
        }
        if (params.count !== undefined) {
            this.statusEl.setAttribute("data-count", String(params.count));
        }

        this.statusEl.textContent = translator.translate(key, params);
        this.statusEl.dataset.tone = tone;
        this.setModalLoading(tone === "loading");
        translator.updateUITranslations();
    }

    public async reloadData() {
        this.isLoading = true;
        this.setBusy(true);
        this.setLoadingStatus();

        try {
            const [menuJson, configsWithQuestionsJson] = await Promise.all([
                this.api.fetchSearchMenu(),
                this.api.fetchReviewConfigsWithQuestions(),
            ]);

            const menuData = isRecord(menuJson) && isRecord(menuJson.data) ? menuJson.data : {};
            this.latestSearchMenuData = menuData;
            this.categories = flattenCategories(menuData);

            this.categories = this.categories.map((category) => {
                const categoryId = toInt(category?.category_id, 0);
                if (categoryId !== OTHERS_CHILD_CATEGORY_ID) return category;

                const parentId = toNumberOrNull(category?.parent_category_id);
                if (parentId === OTHERS_PARENT_CATEGORY_ID) return category;

                return {
                    ...category,
                    parent_category_id: OTHERS_PARENT_CATEGORY_ID,
                };
            });

            this.parents = this.categories
                .filter((c) => toNumberOrNull(c.parent_category_id) === null)
                .sort((a, b) => {
                    const ao = toInt(a.display_order, 999999);
                    const bo = toInt(b.display_order, 999999);
                    if (ao !== bo) return ao - bo;
                    return toInt(a.category_id, 0) - toInt(b.category_id, 0);
                })
                .map((p) =>
                    this.normalizeParentRow({
                        ...p,
                        _isNew: false,
                        _isDeleted: false,
                        _isEditing: false,
                    }),
                );

            this.children = this.categories
                .filter((c) => toNumberOrNull(c.parent_category_id) !== null)
                .sort((a, b) => {
                    const ap = toInt(a.parent_category_id, 0);
                    const bp = toInt(b.parent_category_id, 0);
                    if (ap !== bp) return ap - bp;
                    const ao = toInt(a.display_order, 999999);
                    const bo = toInt(b.display_order, 999999);
                    if (ao !== bo) return ao - bo;
                    return toInt(a.category_id, 0) - toInt(b.category_id, 0);
                })
                .map((c) =>
                    this.normalizeChildRow(
                        (() => {
                            const normalized = {
                                ...c,
                                _isNew: false,
                                _isDeleted: false,
                                _isEditing: false,
                            };
                            normalized._isLocked = this.isLockedOthersChild(normalized);
                            return normalized;
                        })(),
                    ),
                );

            this.configs = extractItems(configsWithQuestionsJson).filter((c) => isRecord(c));

            const configIds = Array.from<number>(
                new Set<number>(
                    this.children
                        .map((c) => toInt(c.review_config_id, 0))
                        .filter((id) => id > 0)
                        .concat(this.configs.map((c) => toInt(c.review_config_id, 0)).filter((id) => id > 0)),
                ),
            ).sort((a: number, b: number) => a - b);

            this.questionsByConfig.clear();
            this.questionById.clear();

            configIds.forEach((configId) => {
                const config = this.configs.find((c) => toInt(c.review_config_id, 0) === configId);
                const list = Array.isArray(config?.questions) ? config.questions : [];
                const sortedList = list
                    .filter((q) => isRecord(q))
                    .sort((a, b) => toInt(a.review_question_id, 0) - toInt(b.review_question_id, 0));

                this.questionsByConfig.set(
                    configId,
                    sortedList.map((q) =>
                        this.normalizeQuestionRow({
                            ...q,
                            _isNew: false,
                            _isDeleted: false,
                            _isEditing: false,
                        }),
                    ),
                );
            });

            this.reindexLocalState();
            this.ensureChildParentSelection();

            this.renderer.renderParentTable(this);
            this.renderer.renderChildTable(this);
            this.renderer.renderReviewTable(this);

            this.setStatus("");
        } catch (error) {
            console.error("[CategoryReviewEditor] Failed to load:", error);
            this.setStatusTranslated("category_review_editor_status_load_failed", "error");
        } finally {
            this.isLoading = false;
            this.setBusy(false);
        }
    }

    public getQuestionLabel(question) {
        return String(
            question?.question_text_ja ||
                question?.question_text_en ||
                question?.question_text_th ||
                question?.question_text ||
                "",
        ).trim();
    }

    public onParentPanelInput(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;

        const field = String(target.dataset.field || "");
        if (field !== "parent_category_name") return;

        const parentId = toInt(target.dataset.parentId, 0);
        if (parentId === 0) return;

        const parent = this.parents.find((p) => toInt(p.category_id, 0) === parentId);
        if (!parent || parent._isDeleted) return;
        if (parent._isLocked) return;

        const selectionStart = target.selectionStart ?? target.value.length;
        const selectionEnd = target.selectionEnd ?? selectionStart;
        parent._isEditing = true;
        parent._draftName = target.value;
        this.renderer.renderParentTable(this);
        this.restoreInputFocus(
            `[data-field="parent_category_name"][data-parent-id="${parentId}"]`,
            target.value.length,
            selectionStart,
            selectionEnd,
        );
    }

    public onChildPanelInput(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;

        const field = String(target.dataset.field || "");
        if (field !== "child_category_name" && field !== "child_review_config_id") return;

        const childId = toInt(target.dataset.childId, 0);
        if (childId === 0) return;

        const child = this.children.find((c) => toInt(c.category_id, 0) === childId);
        if (!child || child._isDeleted) return;
        if (child._isLocked) return;

        const selectionStart = target.selectionStart ?? target.value.length;
        const selectionEnd = target.selectionEnd ?? selectionStart;
        child._isEditing = true;
        if (field === "child_category_name") {
            child._draftName = target.value;
        }
        if (field === "child_review_config_id") {
            const digitsOnly = String(target.value || "").replace(/\D+/g, "");
            if (digitsOnly !== target.value) {
                target.value = digitsOnly;
            }
            child._draftReviewConfigId = toInt(digitsOnly, 0);
        }
        this.renderer.renderChildTable(this);
        this.restoreInputFocus(
            `[data-field="${field}"][data-child-id="${childId}"]`,
            target.value.length,
            selectionStart,
            selectionEnd,
        );
    }

    public onChildPanelChange(event) {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) return;

        const action = String(target.dataset.action || "");
        if (action !== "child-parent-filter") return;

        const parentId = toInt(target.value, 0);
        this.selectedChildParentId = parentId;
        this.renderer.renderChildTable(this);
        this.updateAddEntryModalContext();
        this.setStatus("");
    }

    public onReviewPanelInput(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;

        const field = String(target.dataset.field || "");
        if (field !== "question_text_th" && field !== "question_text_en" && field !== "question_text_ja") {
            return;
        }

        const questionId = toInt(target.dataset.questionId, 0);
        if (questionId === 0) return;

        const question = this.getQuestionRowById(questionId);
        if (!question || question._isDeleted) return;
        if (question._isLocked) return;

        const selectionStart = target.selectionStart ?? target.value.length;
        const selectionEnd = target.selectionEnd ?? selectionStart;
        const configId = this.getConfigIdByQuestionId(questionId);
        question._isEditing = true;
        if (field === "question_text_th") question._draftTextTh = target.value;
        if (field === "question_text_en") question._draftTextEn = target.value;
        if (field === "question_text_ja") question._draftTextJa = target.value;
        this.renderer.renderReviewTable(this);
        this.restoreInputFocus(
            `[data-field="${field}"][data-question-id="${questionId}"][data-config-id="${configId}"]`,
            target.value.length,
            selectionStart,
            selectionEnd,
        );
    }

    public onReviewPanelChange(event) {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) return;

        const field = String(target.dataset.field || "");
        if (field !== "question_data_type") return;

        const questionId = toInt(target.dataset.questionId, 0);
        if (questionId === 0) return;

        const question = this.getQuestionRowById(questionId);
        if (!question || question._isDeleted) return;
        if (question._isLocked) return;

        question._isEditing = true;
        question._draftDataType = String(target.value || "text");
        this.renderer.renderReviewTable(this);
    }

    public async onParentPanelClick(event) {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        const editBtn = this.form.getEditParentButton(target);
        if (editBtn instanceof HTMLElement) {
            const parentId = toInt(editBtn.dataset.parentId, 0);
            if (parentId !== 0) {
                const parent = this.parents.find((p) => toInt(p.category_id, 0) === parentId);
                if (parent && !parent._isDeleted) {
                    if (parent._isLocked) {
                        this.setStatus("This row cannot be edited", "error");
                        return;
                    }
                    if (parent._isEditing) {
                        this.cancelParentEdit(parent);
                        this.renderer.renderParentTable(this);
                        this.setStatus("Edit canceled", "ok");
                        return;
                    }
                    parent._isEditing = true;
                    if (parent._draftName === undefined) {
                        parent._draftName = String(parent.category_name || "");
                    }
                    this.renderer.renderParentTable(this);
                }
            }
            return;
        }

        const deleteBtn = this.form.getDeleteParentButton(target);
        if (deleteBtn instanceof HTMLElement) {
            const parentId = toInt(deleteBtn.dataset.parentId, 0);
            const parent = this.parents.find((p) => toInt(p.category_id, 0) === parentId);
            if (!parent) return;

            if (parent._isDeleted) {
                parent._isDeleted = false;
                this.reindexLocalState();
                this.ensureChildParentSelection();
                this.renderer.renderParentTable(this);
                this.renderer.renderChildTable(this);
                this.renderer.renderReviewTable(this);
                this.setStatus("Delete canceled", "ok");
                return;
            }

            if (!this.canDeleteParentCategory(parent)) {
                this.setStatus("Delete is allowed only when no child categories exist", "error");
                return;
            }

            await this.deleteCategory(parentId);
        }
    }

    public async onChildPanelClick(event) {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        const editBtn = this.form.getEditChildButton(target);
        if (editBtn instanceof HTMLElement) {
            const childId = toInt(editBtn.dataset.childId, 0);
            if (childId !== 0) {
                const child = this.children.find((c) => toInt(c.category_id, 0) === childId);
                if (child && !child._isDeleted) {
                    if (child._isLocked) {
                        this.setStatus("--99 OTHERS cannot be edited", "error");
                        return;
                    }
                    if (child._isEditing) {
                        this.cancelChildEdit(child);
                        this.renderer.renderChildTable(this);
                        this.setStatus("Edit canceled", "ok");
                        return;
                    }
                    child._isEditing = true;
                    if (child._draftName === undefined) {
                        child._draftName = String(child.category_name || "");
                    }
                    this.renderer.renderChildTable(this);
                }
            }
            return;
        }

        const deleteBtn = this.form.getDeleteChildButton(target);
        if (deleteBtn instanceof HTMLElement) {
            const childId = toInt(deleteBtn.dataset.childId, 0);
            const child = this.children.find((c) => toInt(c.category_id, 0) === childId);
            if (!child) return;

            if (child._isDeleted) {
                child._isDeleted = false;
                this.reindexLocalState();
                this.ensureChildParentSelection();
                this.renderer.renderChildTable(this);
                this.renderer.renderReviewTable(this);
                this.setStatus("Delete canceled", "ok");
                return;
            }

            if (!this.canDeleteChildCategory(child)) {
                this.setStatus("Delete is allowed only for 0-item categories", "error");
                return;
            }

            await this.deleteCategory(childId);
            return;
        }

        const reviewConfigBtn = this.form.getOpenReviewSettingsButton(target);
        if (reviewConfigBtn instanceof HTMLElement) {
            const rowEl = this.form.getChildRow(reviewConfigBtn);
            const childId = rowEl instanceof HTMLElement ? toInt(rowEl.dataset.childId, 0) : 0;
            if (childId <= 0) return;

            this.reviewSettingsTargetChildId = childId;

            const configId = toInt(reviewConfigBtn.dataset.configId, 0);
            const safeConfigId = configId > 0 ? configId : this.ensureReviewConfigForChild(childId);
            if (safeConfigId > 0) {
                this.openReviewSettingsModal(safeConfigId);
            }
        }
    }

    public async onReviewPanelClick(event) {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        const rowEl = this.form.getConfigRow(target);
        if (rowEl instanceof HTMLElement) {
            const activeConfigId = toInt(rowEl.dataset.configId, 0);
            if (activeConfigId > 0) {
                this.reviewSettingsConfigId = activeConfigId;
            }
        }

        const selectBtn = this.form.getSelectReviewConfigButton(target);
        if (selectBtn instanceof HTMLElement) {
            const configId = toInt(selectBtn.dataset.configId, 0);
            this.handleReviewSettingsSelectConfig(configId);
            return;
        }

        const optionBtn = this.form.getEditQuestionOptionsButton(target);
        if (optionBtn instanceof HTMLElement) {
            const configId = toInt(optionBtn.dataset.configId, 0);
            const questionId = toInt(optionBtn.dataset.questionId, 0);
            if (configId > 0 && questionId !== 0) {
                this.openQuestionOptionModal(configId, questionId);
            }
            return;
        }

        const addQuestionBtn = this.form.getAddReviewQuestionButton(target);
        if (addQuestionBtn instanceof HTMLElement) {
            const configId = toInt(addQuestionBtn.dataset.configId, 0);
            if (configId > 0) {
                this.reviewSettingsConfigId = configId;
                await this.createReviewQuestion(configId);
            }
            return;
        }

        const removeQuestionBtn = this.form.getRemoveReviewQuestionButton(target);
        if (removeQuestionBtn instanceof HTMLElement) {
            const configId = toInt(removeQuestionBtn.dataset.configId, 0);
            if (configId > 0) {
                const activeQuestions = this.getQuestionsByConfig(configId).filter((q) => !q._isDeleted);
                if (activeQuestions.length <= 0) {
                    this.setStatus("No question to remove", "error");
                    return;
                }

                const newestUnsavedQuestion = [...activeQuestions]
                    .reverse()
                    .find((question) => question?._isNew === true);

                const targetQuestion = newestUnsavedQuestion || activeQuestions[activeQuestions.length - 1];
                const questionId = toInt(targetQuestion?.review_question_id, 0);
                if (questionId !== 0) {
                    await this.deleteReviewQuestion(configId, questionId);
                }
            }
            return;
        }

        const editBtn = this.form.getEditReviewButton(target);
        if (editBtn instanceof HTMLElement) {
            const configId = toInt(editBtn.dataset.configId, 0);
            if (configId > 0) {
                if (this.isReviewConfigEditing(configId)) {
                    this.cancelReviewConfigEdit(configId);
                    this.renderer.renderReviewTable(this);
                    this.setStatus("Edit canceled", "ok");
                    return;
                }
                this.setReviewConfigEditing(configId, true);
                this.renderer.renderReviewTable(this);
            }
            return;
        }

        const deleteBtn = this.form.getDeleteReviewButton(target);
        if (deleteBtn instanceof HTMLElement) {
            const configId = toInt(deleteBtn.dataset.configId, 0);
            if (configId <= 0) return;

            const isDeleted = this.isReviewConfigDeleted(configId);
            this.setReviewConfigDeleted(configId, !isDeleted);
            this.reindexLocalState();
            this.renderer.renderReviewTable(this);
            this.setStatus(isDeleted ? "Delete canceled" : "Marked for delete (not saved)", "ok");
        }
    }

    public async createParentCategory() {
        if (this.isLoading || this.isSubmitting) return;
        const categoryName = `New Parent ${Date.now()}`;

        const parentIds = this.parents
            .filter((parent) => !parent._isDeleted)
            .map((parent) => toInt(parent.category_id, 0))
            .filter((id) => id > 0);

        const othersParentId = OTHERS_PARENT_CATEGORY_ID;
        const maxBelowOthers = parentIds
            .filter((id) => id < othersParentId)
            .reduce((maxId, id) => (id > maxId ? id : maxId), 0);
        const nextParentId = maxBelowOthers + 100;

        if (nextParentId >= othersParentId) {
            this.setStatus(`Cannot allocate new parent_category_id below Others(${othersParentId})`, "error");
            return;
        }

        this.parents.push(
            this.normalizeParentRow({
                category_id: nextParentId,
                category_name: categoryName,
                parent_category_id: null,
                _isNew: true,
                _isDeleted: false,
                _isEditing: true,
                _draftName: categoryName,
            }),
        );
        this.reindexLocalState();
        this.renderer.renderParentTable(this);
        this.setStatus("Added 1 row (not saved)", "ok");
    }

    public async createChildCategory() {
        if (this.isLoading || this.isSubmitting) return;

        const availableParents = this.parents.filter((parent) => !parent._isDeleted);
        if (availableParents.length === 0) {
            this.setStatus("Create a parent category first", "error");
            return;
        }

        const parentId = toInt(availableParents[0]?.category_id, 0);
        if (parentId <= 0 || !this.parentById.has(parentId)) {
            this.setStatus("Invalid parent category ID", "error");
            return;
        }

        const categoryName = `New Child ${Date.now()}`;
        const configId = toInt(this.parentById.get(parentId)?.review_config_id, 0);

        this.children.push(
            this.normalizeChildRow({
                category_id: this.getNextTempCategoryId(),
                category_name: categoryName,
                parent_category_id: parentId,
                review_config_id: configId > 0 ? configId : undefined,
                _isNew: true,
                _isDeleted: false,
                _isEditing: true,
                _draftName: categoryName,
            }),
        );

        this.reindexLocalState();
        this.ensureChildParentSelection();
        this.renderer.renderChildTable(this);
        this.renderer.renderReviewTable(this);
        this.setStatus("Added 1 row (not saved)", "ok");
    }

    public async deleteCategory(categoryId: number) {
        if (this.isLoading || this.isSubmitting) return;

        const parent = this.parents.find((item) => toInt(item.category_id, 0) === categoryId);
        if (parent) {
            if (!this.canDeleteParentCategory(parent)) {
                this.setStatus("Delete is allowed only when no child categories exist", "error");
                return;
            }
            parent._isDeleted = true;
            parent._isEditing = false;
            this.reindexLocalState();
            this.ensureChildParentSelection();
            this.renderer.renderParentTable(this);
            this.renderer.renderChildTable(this);
            this.renderer.renderReviewTable(this);
            this.setStatus("Marked for delete (not saved)", "ok");
            return;
        }

        const child = this.children.find((item) => toInt(item.category_id, 0) === categoryId);
        if (!child) return;

        if (!this.canDeleteChildCategory(child)) {
            this.setStatus("Delete is allowed only for 0-item categories", "error");
            return;
        }

        child._isDeleted = true;
        child._isEditing = false;

        this.reindexLocalState();
        this.ensureChildParentSelection();
        this.renderer.renderChildTable(this);
        this.renderer.renderReviewTable(this);
        this.setStatus("Marked for delete (not saved)", "ok");
    }

    public async createReviewQuestion(forcedConfigId: number = 0) {
        if (this.isLoading || this.isSubmitting) return;

        const preferredConfigId = toInt(forcedConfigId, 0);
        if (preferredConfigId > 0) {
            const preferredList = this.questionsByConfig.get(preferredConfigId) || [];
            const newQuestionId = this.getNextTempQuestionId();
            preferredList.push(
                this.normalizeQuestionRow({
                    review_question_id: newQuestionId,
                    review_config_id: preferredConfigId,
                    question_text: "",
                    question_text_th: "",
                    question_text_en: "",
                    question_text_ja: "",
                    data_type: "text",
                    option_th: [],
                    option_en: [],
                    option_ja: [],
                    is_required: 1,
                    is_active: 1,
                    _isNew: true,
                    _isDeleted: false,
                    _isEditing: true,
                    _draftDataType: "text",
                    _draftOptionTh: [],
                    _draftOptionEn: [],
                    _draftOptionJa: [],
                    _draftTextTh: "",
                    _draftTextEn: "",
                    _draftTextJa: "",
                }),
            );
            this.questionsByConfig.set(preferredConfigId, preferredList);
            this.reindexLocalState();
            this.renderer.renderReviewTable(this);
            this.scrollReviewSettingsTableToRight();
            this.focusNewReviewQuestionEditor(newQuestionId);
            this.setStatus("Added 1 row (not saved)", "ok");
            return;
        }

        const configIds = Array.from<number>(
            new Set<number>(
                this.children
                    .filter((c) => !c._isDeleted)
                    .map((c) => toInt(c.review_config_id, 0))
                    .filter((id) => id > 0),
            ),
        ).sort((a: number, b: number) => a - b);
        if (configIds.length === 0) {
            this.setStatus("No review config found", "error");
            return;
        }

        const configId = toInt(configIds[0], 0);
        if (configId <= 0) {
            this.setStatus("Invalid review config ID", "error");
            return;
        }

        const list = this.questionsByConfig.get(configId) || [];
        const newQuestionId = this.getNextTempQuestionId();
        list.push(
            this.normalizeQuestionRow({
                review_question_id: newQuestionId,
                review_config_id: configId,
                question_text: "",
                question_text_th: "",
                question_text_en: "",
                question_text_ja: "",
                data_type: "text",
                option_th: [],
                option_en: [],
                option_ja: [],
                is_required: 1,
                is_active: 1,
                _isNew: true,
                _isDeleted: false,
                _isEditing: true,
                _draftDataType: "text",
                _draftOptionTh: [],
                _draftOptionEn: [],
                _draftOptionJa: [],
                _draftTextTh: "",
                _draftTextEn: "",
                _draftTextJa: "",
            }),
        );
        this.questionsByConfig.set(configId, list);
        this.reindexLocalState();
        this.renderer.renderReviewTable(this);
        this.scrollReviewSettingsTableToRight();
        this.focusNewReviewQuestionEditor(newQuestionId);
        this.setStatus("Added 1 row (not saved)", "ok");
    }

    public async deleteReviewQuestion(configId: number, questionId: number) {
        if (this.isLoading || this.isSubmitting) return;

        const list = this.questionsByConfig.get(configId) || [];
        const index = list.findIndex((item) => toInt(item.review_question_id, 0) === questionId);
        if (index < 0) return;

        const question = list[index];
        if (question._isNew) {
            list.splice(index, 1);
        } else {
            question._isDeleted = true;
            question._isEditing = false;
        }

        this.questionsByConfig.set(configId, list);
        this.reindexLocalState();
        this.renderer.renderReviewTable(this);
        this.setStatus("Marked for delete (not saved)", "ok");
    }

    public async saveParentCategories(options: UnknownRecord = {}) {
        if (this.isSubmitting) return;
        const { reloadAfterSave = true, suppressNoChangeStatus = false, suppressSuccessStatus = false } = options;

        this.setStatusTranslated("processing");

        try {
            this.isSubmitting = true;
            this.setBusy(true);

            const creates = this.parents.filter((parent) => parent._isNew && !parent._isDeleted);
            const updates = this.parents.filter(
                (parent) =>
                    !parent._isNew &&
                    !parent._isDeleted &&
                    parent._isEditing &&
                    !parent._isLocked &&
                    this.isParentDirty(parent),
            );
            const deletes = this.parents.filter(
                (parent) =>
                    !parent._isNew && parent._isDeleted && !parent._isLocked && this.canDeleteParentCategory(parent),
            );

            if (creates.length === 0 && updates.length === 0 && deletes.length === 0) {
                if (!suppressNoChangeStatus) {
                    this.setStatusTranslated("category_review_editor_status_no_changes", "ok");
                }
                return 0;
            }

            let changedCount = 0;

            for (const parent of creates) {
                const name = String(parent._draftName ?? parent.category_name ?? "").trim();
                if (name === "") {
                    throw new Error(`parent_category_name is required (parent_id=${toInt(parent.category_id, 0)})`);
                }

                await this.api.createCategory({
                    category_id: toInt(parent.category_id, 0),
                    category_name: name,
                    parent_category_id: null,
                });
                changedCount += 1;

                const parentId = toInt(parent.category_id, 0);
                const othersChildId = this.getOthersChildIdFromParentId(parentId);
                const stagedOthersChild = this.children.find(
                    (child) =>
                        !child._isDeleted &&
                        toInt(child.parent_category_id, 0) === parentId &&
                        toInt(child.category_id, 0) === othersChildId,
                );
                const othersName =
                    String(stagedOthersChild?._draftName ?? stagedOthersChild?.category_name ?? OTHERS_NAME).trim() ||
                    OTHERS_NAME;

                await this.api.createCategory({
                    category_id: othersChildId,
                    category_name: othersName,
                    parent_category_id: parentId,
                    review_config_id: 99,
                });
                changedCount += 1;
            }

            for (const parent of updates) {
                const parentId = toInt(parent.category_id, 0);
                const originalName = String(parent.category_name || "");
                const draftName = String(parent._draftName ?? originalName).trim();
                if (draftName === "") {
                    throw new Error(`parent_category_name is required (parent_id=${parentId})`);
                }
                if (draftName === originalName) continue;

                await this.api.updateCategory(parentId, {
                    category_name: draftName,
                });
                changedCount += 1;
            }

            for (const parent of deletes) {
                const parentId = toInt(parent.category_id, 0);
                if (parentId <= 0) continue;
                await this.api.deleteCategory(parentId);
                changedCount += 1;
            }

            if (changedCount === 0) {
                if (!suppressNoChangeStatus) {
                    this.setStatusTranslated("category_review_editor_status_no_changes", "ok");
                }
                return 0;
            }

            if (!suppressSuccessStatus) {
                this.setStatusTranslated("category_review_editor_status_category_saved", "ok", { count: changedCount });
            }
            if (reloadAfterSave) {
                await this.reloadData();
            }
            return changedCount;
        } catch (error) {
            console.error("[CategoryReviewEditor] saveParentCategories failed:", error);
            this.setStatusTranslated("category_review_editor_status_category_save_failed", "error");
            return 0;
        } finally {
            this.isSubmitting = false;
            this.setBusy(false);
        }
    }

    public async saveChildCategories(options: UnknownRecord = {}) {
        if (this.isSubmitting) return;
        const { reloadAfterSave = true, suppressNoChangeStatus = false, suppressSuccessStatus = false } = options;

        this.setStatusTranslated("processing");

        try {
            this.isSubmitting = true;
            this.setBusy(true);

            const creates = this.children.filter(
                (child) => child._isNew && !child._isDeleted && !child._isSystemOthers,
            );
            const updates = this.children.filter(
                (child) => !child._isNew && !child._isDeleted && !child._isLocked && this.isChildDirty(child),
            );
            const deletes = this.children.filter(
                (child) =>
                    !child._isNew &&
                    child._isDeleted &&
                    !child._isLocked &&
                    !child._isSystemOthers &&
                    this.canDeleteChildCategory(child),
            );

            if (creates.length === 0 && updates.length === 0 && deletes.length === 0) {
                if (!suppressNoChangeStatus) {
                    this.setStatusTranslated("category_review_editor_status_no_changes", "ok");
                }
                return 0;
            }

            let changedCount = 0;

            for (const child of creates) {
                const draftName = String(child._draftName ?? child.category_name ?? "").trim();
                const parentId = toInt(child.parent_category_id, 0);
                const configId = toInt(child._draftReviewConfigId ?? child.review_config_id ?? 0, 0);

                if (draftName === "") {
                    throw new Error(`child_category_name is required (child_id=${toInt(child.category_id, 0)})`);
                }
                if (parentId <= 0) {
                    throw new Error("parent_category_id is required for child category");
                }

                const payload: UnknownRecord = {
                    category_id: toInt(child.category_id, 0),
                    category_name: draftName,
                    parent_category_id: parentId,
                };
                if (configId > 0) {
                    payload.review_config_id = configId;
                }

                await this.api.createCategory(payload);
                changedCount += 1;
            }

            for (const child of updates) {
                const childId = toInt(child.category_id, 0);
                const originalName = String(child.category_name || "");
                const draftName = String(child._draftName ?? originalName).trim();
                const originalConfigId = toInt(child.review_config_id, 0);
                const draftConfigId = toInt(child._draftReviewConfigId ?? child.review_config_id ?? 0, 0);

                if (draftName === "") {
                    throw new Error(`child_category_name is required (child_id=${childId})`);
                }

                const payload: UnknownRecord = {};
                if (draftName !== originalName) {
                    payload.category_name = draftName;
                }
                if (draftConfigId > 0 && draftConfigId !== originalConfigId) {
                    payload.review_config_id = draftConfigId;
                }
                if (Object.keys(payload).length === 0) continue;

                await this.api.updateCategory(childId, payload);
                changedCount += 1;
            }

            for (const child of deletes) {
                const childId = toInt(child.category_id, 0);
                if (childId <= 0) continue;
                await this.api.deleteCategory(childId);
                changedCount += 1;
            }

            if (changedCount === 0) {
                if (!suppressNoChangeStatus) {
                    this.setStatusTranslated("category_review_editor_status_no_changes", "ok");
                }
                return 0;
            }

            if (!suppressSuccessStatus) {
                this.setStatusTranslated("category_review_editor_status_category_saved", "ok", { count: changedCount });
            }
            if (reloadAfterSave) {
                await this.reloadData();
            }
            return changedCount;
        } catch (error) {
            console.error("[CategoryReviewEditor] saveChildCategories failed:", error);
            this.setStatusTranslated("category_review_editor_status_category_save_failed", "error");
            notifier.error(toErrorMessage(error, "category_review_editor_status_category_save_failed"));
            return 0;
        } finally {
            this.isSubmitting = false;
            this.setBusy(false);
        }
    }

    public async saveReviewConfigs(options: UnknownRecord = {}) {
        if (this.isSubmitting) return;
        const { reloadAfterSave = true, suppressNoChangeStatus = false, suppressSuccessStatus = false } = options;

        this.setStatusTranslated("processing");

        try {
            this.isSubmitting = true;
            this.setBusy(true);

            const creates = this.configs.filter((config) => config?._isNew === true && config?._isDeleted !== true);

            if (creates.length === 0) {
                if (!suppressNoChangeStatus) {
                    this.setStatusTranslated("category_review_editor_status_no_changes", "ok");
                }
                return 0;
            }

            let changedCount = 0;

            for (const config of creates) {
                const localId = toInt(config.review_config_id, 0);
                const beforeConfigIds = new Set(
                    this.configs
                        .filter((item) => item?._isNew !== true && item?._isDeleted !== true)
                        .map((item) => toInt(item?.review_config_id, 0))
                        .filter((id) => id > 0),
                );

                const response = await this.api.createReviewConfig({
                    name: String(config.name || `Review Config ${localId}`),
                    description: String(config.description || config.name || `Review Config ${localId}`),
                    is_active: toInt(config.is_active, 1),
                });

                let json = null;
                try {
                    json = await response.json();
                } catch (_error) {
                    json = null;
                }

                let { id: createdId, data } = this.extractReviewConfigIdFromCreateResponse(json);

                if (createdId <= 0) {
                    const refreshed = await this.api.fetchReviewConfigsWithQuestions();
                    const refreshedConfigs = extractItems(refreshed).filter((item) => isRecord(item));
                    const appended = refreshedConfigs
                        .map((item) => toInt(item.review_config_id, 0))
                        .filter((id) => id > 0 && !beforeConfigIds.has(id))
                        .sort((a, b) => a - b);

                    if (appended.length > 0) {
                        createdId = appended[0];
                        const created = refreshedConfigs.find((item) => toInt(item.review_config_id, 0) === createdId);
                        if (isRecord(created)) {
                            data = created;
                        }
                    }
                }

                if (createdId <= 0) {
                    throw new Error("Failed to resolve created review_config_id");
                }

                config._isNew = false;
                config.name = String(data?.name || config.name || `Review Config ${createdId}`);
                config.description = String(data?.description || config.description || "");
                config.is_active = toInt(data?.is_active ?? config.is_active ?? 1, 1);

                if (createdId !== localId) {
                    this.remapReviewConfigIdInLocalState(localId, createdId);
                }

                changedCount += 1;
            }

            if (changedCount === 0) {
                if (!suppressNoChangeStatus) {
                    this.setStatusTranslated("category_review_editor_status_no_changes", "ok");
                }
                return 0;
            }

            if (!suppressSuccessStatus) {
                this.setStatusTranslated("category_review_editor_status_review_config_saved", "ok", {
                    count: changedCount,
                });
            }
            if (reloadAfterSave) {
                await this.reloadData();
            }
            return changedCount;
        } catch (error) {
            console.error("[CategoryReviewEditor] saveReviewConfigs failed:", error);
            this.setStatusTranslated("category_review_editor_status_review_config_save_failed", "error");
            notifier.error(toErrorMessage(error, "category_review_editor_status_review_config_save_failed"));
            return 0;
        } finally {
            this.isSubmitting = false;
            this.setBusy(false);
        }
    }

    public async saveReviewQuestions(options: UnknownRecord = {}) {
        if (this.isSubmitting) return;
        const { reloadAfterSave = true, suppressNoChangeStatus = false, suppressSuccessStatus = false } = options;

        this.setStatusTranslated("processing");

        try {
            this.isSubmitting = true;
            this.setBusy(true);

            const creates = [];
            const updates = [];
            const deletes = [];

            this.questionsByConfig.forEach((list, configId) => {
                const safeConfigId = toInt(configId, 0);
                if (safeConfigId <= 0) return;

                list.forEach((question, index) => {
                    const questionId = toInt(question.review_question_id, 0);
                    const textTh = String(
                        question._draftTextTh ?? question.question_text_th ?? question.question_text ?? "",
                    ).trim();
                    const textEn = String(
                        question._draftTextEn ?? question.question_text_en ?? question.question_text ?? "",
                    ).trim();
                    const textJa = String(
                        question._draftTextJa ?? question.question_text_ja ?? question.question_text ?? "",
                    ).trim();
                    const baseText = textJa || textEn || textTh;
                    const dataType = String(question._draftDataType ?? question.data_type ?? "text").trim() || "text";
                    const optionTh = this.getQuestionOptionsByLang(question, "th");
                    const optionEn = this.getQuestionOptionsByLang(question, "en");
                    const optionJa = this.getQuestionOptionsByLang(question, "ja");
                    const baseOptions = optionJa.length > 0 ? optionJa : optionEn.length > 0 ? optionEn : optionTh;
                    const rowRef = questionId > 0 ? `question_id=${questionId}` : `new_question_index=${index + 1}`;
                    if (textTh === "" || textEn === "" || textJa === "") {
                        throw new Error(`question_text_th/en/ja is required (${rowRef}, config_id=${safeConfigId})`);
                    }

                    if (question._isDeleted) {
                        if (!question._isNew && questionId > 0) {
                            deletes.push({ configId: safeConfigId, questionId });
                        }
                        return;
                    }

                    if (question._isNew) {
                        creates.push({
                            configId: safeConfigId,
                            payload: {
                                question_text: baseText,
                                question_text_th: textTh || baseText,
                                question_text_en: textEn || baseText,
                                question_text_ja: textJa || baseText,
                                data_type: dataType,
                                options: baseOptions,
                                option_th: optionTh,
                                option_en: optionEn,
                                option_ja: optionJa,
                                is_required: 1,
                                is_active: 1,
                                sort_order: index + 1,
                            },
                        });
                        return;
                    }

                    if (!question._isEditing || !this.isQuestionDirty(question)) return;

                    const isActive = toInt(question.is_active, 1) === 1;
                    if (isActive && (textTh === "" || textEn === "" || textJa === "")) {
                        throw new Error(
                            `question_text_th/en/ja is required for active question (question_id=${questionId}, config_id=${safeConfigId})`,
                        );
                    }

                    updates.push({
                        configId: safeConfigId,
                        questionId,
                        payload: {
                            question_text: baseText,
                            question_text_th: textTh || baseText,
                            question_text_en: textEn || baseText,
                            question_text_ja: textJa || baseText,
                            data_type: dataType,
                            options: baseOptions,
                            option_th: optionTh,
                            option_en: optionEn,
                            option_ja: optionJa,
                        },
                    });
                });
            });

            if (creates.length === 0 && updates.length === 0 && deletes.length === 0) {
                if (!suppressNoChangeStatus) {
                    this.setStatusTranslated("category_review_editor_status_no_changes", "ok");
                }
                return 0;
            }

            let changedCount = 0;

            for (const item of creates) {
                await this.api.createReviewQuestion(item.configId, item.payload);
                changedCount += 1;
            }

            for (const item of updates) {
                await this.api.updateReviewQuestion(item.configId, item.questionId, item.payload);
                changedCount += 1;
            }

            for (const item of deletes) {
                await this.api.deleteReviewQuestion(item.configId, item.questionId);
                changedCount += 1;
            }

            if (changedCount === 0) {
                if (!suppressNoChangeStatus) {
                    this.setStatusTranslated("category_review_editor_status_no_changes", "ok");
                }
                return 0;
            }

            if (!suppressSuccessStatus) {
                this.setStatusTranslated("category_review_editor_status_question_saved", "ok", { count: changedCount });
            }
            if (reloadAfterSave) {
                await this.reloadData();
            }
            return changedCount;
        } catch (error) {
            console.error("[CategoryReviewEditor] saveReviewQuestions failed:", error);
            this.setStatusTranslated("category_review_editor_status_question_save_failed", "error");
            notifier.error(toErrorMessage(error, "category_review_editor_status_question_save_failed"));
            return 0;
        } finally {
            this.isSubmitting = false;
            this.setBusy(false);
        }
    }

    public getQuestionsByConfig(configId: number) {
        const safeConfigId = toInt(configId, 0);
        if (safeConfigId <= 0) return [];
        return (this.questionsByConfig.get(safeConfigId) || []).filter((q) => isRecord(q));
    }

    public isReviewConfigEditing(configId: number): boolean {
        return this.getQuestionsByConfig(configId).some((q) => !q._isDeleted && q._isEditing === true);
    }

    public isReviewConfigDirty(configId: number): boolean {
        return this.getQuestionsByConfig(configId).some((q) => !q._isDeleted && this.isQuestionDirty(q));
    }

    public isReviewConfigNew(configId: number): boolean {
        return this.getQuestionsByConfig(configId).some((q) => !q._isDeleted && q._isNew === true);
    }

    public isReviewConfigDeleted(configId: number): boolean {
        const list = this.getQuestionsByConfig(configId);
        if (list.length === 0) return false;
        return list.every((q) => q._isDeleted === true);
    }

    public setReviewConfigEditing(configId, isEditing) {
        this.getQuestionsByConfig(configId).forEach((question) => {
            if (question._isDeleted) return;
            question._isEditing = isEditing;
            if (!isEditing) return;

            if (question._draftTextTh === undefined) {
                question._draftTextTh = String(question.question_text_th ?? question.question_text ?? "");
            }
            if (question._draftTextEn === undefined) {
                question._draftTextEn = String(question.question_text_en ?? question.question_text ?? "");
            }
            if (question._draftTextJa === undefined) {
                question._draftTextJa = String(question.question_text_ja ?? question.question_text ?? "");
            }
            if (question._draftDataType === undefined) {
                question._draftDataType = String(question.data_type ?? "text");
            }
            if (question._draftOptionTh === undefined) {
                question._draftOptionTh = Array.isArray(question.option_th) ? [...question.option_th] : [];
            }
            if (question._draftOptionEn === undefined) {
                question._draftOptionEn = Array.isArray(question.option_en) ? [...question.option_en] : [];
            }
            if (question._draftOptionJa === undefined) {
                question._draftOptionJa = Array.isArray(question.option_ja) ? [...question.option_ja] : [];
            }
        });
    }

    public cancelReviewConfigEdit(configId) {
        this.getQuestionsByConfig(configId).forEach((question) => {
            this.cancelQuestionEdit(question);
        });
    }

    public setReviewConfigDeleted(configId, isDeleted) {
        this.getQuestionsByConfig(configId).forEach((question) => {
            question._isDeleted = isDeleted;
            if (isDeleted) question._isEditing = false;
        });
    }
}
